import { action } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';

export interface IAgtLinkedinModel {
	vtnrId: number;
	bm: number;
	accountUrl: string;
	accountStatus: number;
	follower: number;
	sichtkontakte: number;
	erreichtePersonen: number;
	postinganzahl: number;
}

export class AgtLinkedinModel implements IAgtLinkedinModel {
	vtnrId: number;
	bm: number;
	accountUrl: string;
	accountStatus: number;
	follower: number;
	sichtkontakte: number;
	erreichtePersonen: number;
	postinganzahl: number;

	// Legacy compatibility properties
	agtId: number;
	j: any;

	constructor(data: any) {
		this.vtnrId = data.aid || 0;
		this.bm = data.bm ? parseInt(data.bm) : 0;
		this.accountUrl = data.u || '';
		this.accountStatus = data.s || 0;
		this.follower = data.f || 0;
		this.sichtkontakte = data.v || 0;
		this.erreichtePersonen = data.r || 0;
		this.postinganzahl = data.p || 0;

		// Legacy compatibility - alias properties
		this.agtId = this.vtnrId;

		// Legacy j property for backwards compatibility
		this.j = {
			u: this.accountUrl,
			s: this.accountStatus,
			f: this.follower,
			v: this.sichtkontakte,
			r: this.erreichtePersonen,
			p: this.postinganzahl
		};
	}
}

export class AgtLinkedinStore extends AgtAnalyticsStore {
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'agentur_linkedin',
			defaultColumns: [
				{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid' },
				{ column: 'bm', aggregateFunction: AggregateFunction.NONE },
				{ column: 'account_url', aggregateFunction: AggregateFunction.NONE, alias: 'u' },
				{ column: 'account_status', aggregateFunction: AggregateFunction.NONE, alias: 's' },
				{ column: 'follower', aggregateFunction: AggregateFunction.NONE, alias: 'f' },
				{ column: 'sichtkontakte', aggregateFunction: AggregateFunction.NONE, alias: 'v' },
				{ column: 'erreichte_personen', aggregateFunction: AggregateFunction.NONE, alias: 'r' },
				{ column: 'postinganzahl', aggregateFunction: AggregateFunction.NONE, alias: 'p' },
			],
		});
		this.queryName = '?linkedin';
	}

	async getDistinctBMs(): Promise<number[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'bm', aggregateFunction: AggregateFunction.NONE, distinct: true },
		];
		const results = await this.query(selectColumns);
		return results.map(data => parseInt(data.bm)).sort((a, b) => a - b);
	}

	@action
	async loadAgtByBm(agtId: number, bm: number): Promise<AgtLinkedinModel[]> {
		if (!bm || bm <= 0) {
			return [];
		}
		let whereClause = 'a.vtnr_id = ' + agtId + ' AND bm = ' + bm;
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map(data => new AgtLinkedinModel(data));
	}

	@action
	async aggregateYtd(bm: number): Promise<any[]> {
		let firstBmOfYear = Math.floor(bm / 100) * 100 + 1;
		let whereClause = `bm >= ${firstBmOfYear} AND bm <= ${bm}`;
		if (!bm || bm <= 0) {
			return [];
		}
		const selectColumns: SelectColumn[] = [
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid', groupBy: true },
			{ column: 'postinganzahl', aggregateFunction: AggregateFunction.SUM, alias: 'p', },
			{ column: 'sichtkontakte', aggregateFunction: AggregateFunction.SUM, alias: 'v', },
			{ column: 'erreichte_personen', aggregateFunction: AggregateFunction.SUM, alias: 'r', },
		];

		return await this.query(selectColumns, whereClause);
	}

	@action
	async loadByBmRange(minBm: number, maxBm: number): Promise<AgtLinkedinModel[]> {
		if (!minBm || !maxBm || minBm <= 0 || maxBm <= 0) {
			return [];
		}
		// raw monthly rows, no ytd merge -> sums are computed client-side over the range
		const whereClause = ` bm >= ${minBm} AND bm <= ${maxBm} `;
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map((data) => new AgtLinkedinModel(data));
	}

	@action
	async loadByBm(bm: number): Promise<AgtLinkedinModel[]> {
		if (!bm || bm <= 0) {
			return [];
		}
		let whereClause = ` bm = ${bm} `;

		const [results, aggForBM] = await Promise.all([
			this.query(this.config.defaultColumns, whereClause),
			this.aggregateYtd(bm)
		]);

		// Merge aggregated YTD data into results where aid matches and bm equals bm
		return results.map(data => {
			if (data.bm === bm) {
				const agg = aggForBM.find(a => a.aid === data.aid);
				if (agg) {
					// Merge aggregated values into the result
					return new AgtLinkedinModel({
						...data,
						p: agg.p, // postinganzahl ytd
						v: agg.v, // sichtkontakte ytd
						r: agg.r  // erreichte_personen ytd
					});
				}
			}
			return new AgtLinkedinModel(data);
		});
	}
}
